import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL, BACKEND_URL } from '@/utils/api';
import { DownloadButton } from '@/utils/DownloadButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { 
  FileText, 
  Calendar as CalendarIcon, 
  Download, 
  Filter,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Clock,
  AlertTriangle,
  Search,
  Eye,
  Edit,
  Trash2,
  LogIn,
  Coffee,
  RotateCcw,
  LogOut,
  Save,
  Image,
  X,
  Camera,
  CheckCircle,
  XCircle,
  FileCheck,
  Loader2,
  ChevronRight,
  Shield,
  ListChecks,
  MapPin
} from 'lucide-react';

const API = API_URL;

export default function ReportsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId, loading: tenantLoading } = useTenant();
  const [date, setDate] = useState(new Date());
  const [report, setReport] = useState({ 
    date: '', 
    employees: [], 
    summary: { totalEmployees: 0, present: 0, absent: 0, late: 0, totalWorkedHours: 0, totalBreakMinutes: 0 } 
  });
  const [sites, setSites] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedSite, setSelectedSite] = useState('all');
  const [selectedZone, setSelectedZone] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Photo preview dialog
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoTitle, setPhotoTitle] = useState('');
  
  // Employee details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Punch correction dialog
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [selectedPunch, setSelectedPunch] = useState(null);
  const [correctionData, setCorrectionData] = useState({
    newTimestamp: '',
    newEventType: '',
    reason: ''
  });
  
  // Validation states
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationComment, setValidationComment] = useState('');
  const [validationAction, setValidationAction] = useState(''); // approve, reject
  const [validating, setValidating] = useState(false);
  const [dailyRecords, setDailyRecords] = useState({});
  const [approvalFilter, setApprovalFilter] = useState('all'); // all, pending, approved, rejected

  const fetchSitesAndZones = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    try {
      const [sitesRes, zonesRes] = await Promise.all([
        axios.get(`${API}/sites?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/zones?tenantId=${effectiveTenantId}`)
      ]);
      setSites(sitesRes.data);
      setZones(zonesRes.data);
    } catch (error) {
      console.error('Failed to fetch sites/zones:', error);
    }
  }, [effectiveTenantId]);

  const fetchReport = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      let url = `${API}/reports/daily?tenantId=${effectiveTenantId}&date=${dateStr}`;
      
      if (selectedSite !== 'all') url += `&siteId=${selectedSite}`;
      if (selectedZone !== 'all') url += `&zoneId=${selectedZone}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, date, selectedSite, selectedZone, searchQuery, t]);

  useEffect(() => {
    fetchSitesAndZones();
  }, [fetchSitesAndZones]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Fetch daily records for validation
  const fetchDailyRecords = useCallback(async () => {
    if (!effectiveTenantId) return;
    
    try {
      const token = localStorage.getItem('punchtm-token');
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API}/daily-records?tenantId=${effectiveTenantId}&date=${dateStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Create a map of employeeId -> record for easy lookup
      const recordsMap = {};
      response.data.records?.forEach(r => {
        recordsMap[r.employeeId] = r;
      });
      setDailyRecords(recordsMap);
    } catch (error) {
      console.error('Failed to fetch daily records:', error);
    }
  }, [effectiveTenantId, date]);

  useEffect(() => {
    fetchDailyRecords();
  }, [fetchDailyRecords]);

  // Create daily record if it doesn't exist
  const ensureDailyRecord = async (employeeId) => {
    const token = localStorage.getItem('punchtm-token');
    const dateStr = format(date, 'yyyy-MM-dd');
    
    try {
      const response = await axios.post(
        `${API}/daily-records/create?tenantId=${effectiveTenantId}&employeeId=${employeeId}&date=${dateStr}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create daily record:', error);
      throw error;
    }
  };

  // Handle validation
  const handleValidation = async (action) => {
    if (selectedRecords.length === 0) {
      toast.error(language === 'fr' ? 'Sélectionnez au moins un employé' : 'Select at least one employee');
      return;
    }
    
    setValidationAction(action);
    setValidationComment('');
    setValidationDialogOpen(true);
  };

  const submitValidation = async () => {
    if (validationAction === 'rejected' && !validationComment.trim()) {
      toast.error(language === 'fr' ? 'Un commentaire est obligatoire pour rejeter' : 'Comment is required for rejection');
      return;
    }
    
    setValidating(true);
    const token = localStorage.getItem('punchtm-token');
    const dateStr = format(date, 'yyyy-MM-dd');
    
    try {
      // First, ensure all records exist in database
      const recordIds = [];
      for (const empId of selectedRecords) {
        // Always call ensureDailyRecord to make sure the record exists in DB
        const record = await ensureDailyRecord(empId);
        recordIds.push(record.id);
      }
      
      // Bulk approve/reject
      await axios.post(
        `${API}/daily-records/bulk-approve`,
        {
          recordIds,
          status: validationAction,
          comment: validationComment || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(
        validationAction === 'approved' 
          ? (language === 'fr' ? 'Heures validées' : 'Hours approved')
          : (language === 'fr' ? 'Heures rejetées' : 'Hours rejected')
      );
      
      setValidationDialogOpen(false);
      setSelectedRecords([]);
      setValidationComment('');
      fetchDailyRecords();
    } catch (error) {
      console.error('Validation failed:', error);
      toast.error(t('error'));
    } finally {
      setValidating(false);
    }
  };

  // Toggle record selection
  const toggleRecordSelection = (employeeId) => {
    setSelectedRecords(prev => 
      prev.includes(employeeId) 
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Select all records
  const toggleSelectAll = () => {
    if (selectedRecords.length === filteredEmployees.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredEmployees.map(emp => emp.employeeId));
    }
  };

  // Get approval status badge — enhanced with validator info
  const getApprovalBadge = (employeeId) => {
    const record = dailyRecords[employeeId];
    const status = record?.approvalStatus || 'pending';
    
    switch (status) {
      case 'approved':
        return (
          <div className="flex flex-col items-start gap-0.5">
            <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
              <CheckCircle size={11} className="mr-1" />
              {language === 'fr' ? 'Validé' : 'Approved'}
            </Badge>
            {record?.approvedByName && (
              <span className="text-[10px] text-slate-400 dark:text-slate-500 truncate max-w-24" title={`${record.approvedByName} — ${record.approvedAt ? format(new Date(record.approvedAt), 'dd/MM HH:mm') : ''}`}>
                {record.approvedByName.split(' ')[0]} • {record.approvedAt ? format(new Date(record.approvedAt), 'HH:mm') : ''}
              </span>
            )}
          </div>
        );
      case 'rejected':
        return (
          <div className="flex flex-col items-start gap-0.5">
            <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
              <XCircle size={11} className="mr-1" />
              {language === 'fr' ? 'Rejeté' : 'Rejected'}
            </Badge>
            {record?.approvedByName && (
              <span className="text-[10px] text-red-400 dark:text-red-500 truncate max-w-24" title={record.approvalComment || ''}>
                {record.approvedByName.split(' ')[0]} • {record.approvedAt ? format(new Date(record.approvedAt), 'HH:mm') : ''}
              </span>
            )}
          </div>
        );
      default:
        return (
          <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            <Clock size={11} className="mr-1" />
            {language === 'fr' ? 'En attente' : 'Pending'}
          </Badge>
        );
    }
  };

  const handleDateSelect = (newDate) => {
    if (newDate) {
      setDate(newDate);
      setCalendarOpen(false);
    }
  };

  // Build export URLs for direct <a> links
  const buildExportUrl = (type) => {
    if (!effectiveTenantId) return '#';
    const dateStr = format(date, 'yyyy-MM-dd');
    let url = `${API}/export/${type}?tenantId=${effectiveTenantId}&date=${dateStr}`;
    if (selectedSite !== 'all') url += `&siteId=${selectedSite}`;
    if (selectedZone !== 'all') url += `&zoneId=${selectedZone}`;
    return url;
  };

  const handleViewPhoto = (photoUrl, title) => {
    if (!photoUrl) return;
    setSelectedPhoto(`${BACKEND_URL}${photoUrl}`);
    setPhotoTitle(title);
    setPhotoDialogOpen(true);
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setDetailsDialogOpen(true);
  };

  const handleOpenCorrection = (punch) => {
    setSelectedPunch(punch);
    setCorrectionData({
      newTimestamp: punch.timestamp ? punch.timestamp.slice(0, 16) : '',
      newEventType: punch.eventType,
      reason: ''
    });
    setCorrectionDialogOpen(true);
  };

  const handleCorrectPunch = async () => {
    if (!correctionData.reason.trim()) {
      toast.error(language === 'fr' ? 'Raison requise' : 'Reason required');
      return;
    }

    try {
      await axios.put(`${API}/punch/${selectedPunch.id}/correct`, {
        punchId: selectedPunch.id,
        newTimestamp: correctionData.newTimestamp ? `${correctionData.newTimestamp}:00+00:00` : null,
        newEventType: correctionData.newEventType !== selectedPunch.eventType ? correctionData.newEventType : null,
        reason: correctionData.reason
      });
      
      toast.success(language === 'fr' ? 'Punch corrigé' : 'Punch corrected');
      setCorrectionDialogOpen(false);
      fetchReport();
    } catch (error) {
      console.error('Failed to correct punch:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeletePunch = async (punch) => {
    const reason = window.prompt(language === 'fr' ? 'Raison de suppression:' : 'Reason for deletion:');
    if (!reason) return;

    try {
      await axios.delete(`${API}/punch/${punch.id}?reason=${encodeURIComponent(reason)}`);
      toast.success(language === 'fr' ? 'Punch supprimé' : 'Punch deleted');
      fetchReport();
    } catch (error) {
      console.error('Failed to delete punch:', error);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return isoString.substring(11, 16);
  };

  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getPunchTypeLabel = (type) => {
    const labels = {
      IN: language === 'fr' ? 'Entrée' : 'Clock In',
      BREAK_START: language === 'fr' ? 'Pause' : 'Break',
      BREAK_END: language === 'fr' ? 'Retour' : 'Return',
      OUT: language === 'fr' ? 'Sortie' : 'Clock Out'
    };
    return labels[type] || type;
  };

  const getPunchIcon = (type) => {
    const icons = {
      IN: <LogIn size={16} className="text-green-600" />,
      BREAK_START: <Coffee size={16} className="text-orange-600" />,
      BREAK_END: <RotateCcw size={16} className="text-blue-600" />,
      OUT: <LogOut size={16} className="text-red-600" />
    };
    return icons[type] || null;
  };

  const PhotoThumbnail = ({ photoUrl, title, employeeName }) => {
    if (!photoUrl) {
      return <span className="text-slate-400">—</span>;
    }
    return (
      <button
        onClick={() => handleViewPhoto(photoUrl, `${title} - ${employeeName}`)}
        className="relative group"
      >
        <img 
          src={`${BACKEND_URL}${photoUrl}`} 
          alt={title}
          className="w-10 h-10 rounded object-cover border border-slate-200 hover:border-primary transition-colors"
        />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
          <Eye size={14} className="text-white" />
        </div>
      </button>
    );
  };

  // Filter employees by approval status — must be defined before approvalCounts
  const filteredEmployees = (report.employees || []).filter(emp => {
    if (approvalFilter === 'all') return true;
    const record = dailyRecords[emp.employeeId];
    const status = record?.approvalStatus || 'pending';
    return status === approvalFilter;
  });

  // Handle "Validate Day" — select all pending and approve
  const handleValidateDay = () => {
    const pending = filteredEmployees.filter(emp => {
      const record = dailyRecords[emp.employeeId];
      return (record?.approvalStatus || 'pending') === 'pending';
    });
    if (pending.length === 0) {
      toast.info(language === 'fr' ? 'Aucune fiche en attente' : 'No pending records');
      return;
    }
    setSelectedRecords(pending.map(e => e.employeeId));
    setValidationAction('approved');
    setValidationComment('');
    setValidationDialogOpen(true);
  };

  // Approval counts
  const approvalCounts = {
    pending: filteredEmployees.filter(e => (dailyRecords[e.employeeId]?.approvalStatus || 'pending') === 'pending').length,
    approved: filteredEmployees.filter(e => dailyRecords[e.employeeId]?.approvalStatus === 'approved').length,
    rejected: filteredEmployees.filter(e => dailyRecords[e.employeeId]?.approvalStatus === 'rejected').length,
  };

  const summaryCards = [
    { label: language === 'fr' ? 'Employés' : 'Employees', value: report.summary?.totalEmployees || 0, icon: Users, color: 'text-slate-600 bg-slate-100', borderColor: 'border-slate-200' },
    { label: language === 'fr' ? 'Présents' : 'Present', value: report.summary?.present || 0, icon: UserCheck, color: 'text-green-600 bg-green-100', borderColor: 'border-green-200' },
    { label: language === 'fr' ? 'Absents' : 'Absent', value: report.summary?.absent || 0, icon: UserX, color: 'text-red-600 bg-red-100', borderColor: 'border-red-200' },
    { label: language === 'fr' ? 'Retards' : 'Late', value: report.summary?.late || 0, icon: AlertTriangle, color: 'text-amber-600 bg-amber-100', borderColor: 'border-amber-200' },
    { label: language === 'fr' ? 'Heures totales' : 'Total Hours', value: `${report.summary?.totalWorkedHours || 0}h`, icon: Clock, color: 'text-blue-600 bg-blue-100', borderColor: 'border-blue-200' },
  ];

  return (
    <div className="space-y-4" data-testid="reports-page">
      {/* Header — compact with global actions */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{t('reports')}</h1>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 h-8" data-testid="date-picker-btn">
                <CalendarIcon className="h-3.5 w-3.5" />
                {format(date, 'dd MMM yyyy', { locale: language === 'fr' ? fr : enUS })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={handleDateSelect} initialFocus locale={language === 'fr' ? fr : enUS} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center gap-2">
          {/* Global validation buttons */}
          <Button
            size="sm"
            className="gap-1.5 bg-green-600 hover:bg-green-700 h-8"
            onClick={handleValidateDay}
            disabled={approvalCounts.pending === 0}
            data-testid="validate-day-btn"
          >
            <ListChecks size={14} />
            {language === 'fr' ? 'Valider la journée' : 'Validate Day'}
            {approvalCounts.pending > 0 && <Badge className="bg-white/20 text-white ml-1 text-[10px] h-4 px-1">{approvalCounts.pending}</Badge>}
          </Button>
          <DownloadButton url={buildExportUrl('excel')} className="gap-1 h-8" size="sm" data-testid="export-excel-btn">
            <Download size={14} /> Excel
          </DownloadButton>
          <DownloadButton url={buildExportUrl('pdf')} className="gap-1 h-8" size="sm" data-testid="export-pdf-btn">
            <Download size={14} /> PDF
          </DownloadButton>
        </div>
      </div>

      {/* Summary KPIs — compact row */}
      <div className="grid grid-cols-5 gap-2">
        {summaryCards.map((stat, index) => (
          <div key={index} className={`flex items-center gap-2 p-2.5 rounded-lg border ${stat.borderColor} dark:border-opacity-40`}>
            <div className={`p-1.5 rounded-md ${stat.color} dark:bg-opacity-30`}>
              <stat.icon size={16} />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900 dark:text-white leading-none">{stat.value}</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar + validation status tabs */}
      <div className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900/60 rounded-lg border dark:border-slate-800" data-testid="filter-bar">
        <Filter size={14} className="text-slate-400 ml-1" />
        <div className="relative flex-1 max-w-40">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-7 h-8 text-xs" data-testid="search-input" />
        </div>
        <Select value={selectedSite} onValueChange={setSelectedSite}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="site-filter">
            <MapPin size={12} className="mr-1 text-slate-400" />
            <SelectValue placeholder={t('site')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'fr' ? 'Tous sites' : 'All sites'}</SelectItem>
            {sites.map((site) => (<SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={selectedZone} onValueChange={setSelectedZone}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="zone-filter">
            <SelectValue placeholder={language === 'fr' ? 'Zone' : 'Zone'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'fr' ? 'Toutes zones' : 'All zones'}</SelectItem>
            {zones.map((zone) => (<SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex border rounded-md overflow-hidden ml-auto" data-testid="approval-tabs">
          {[
            { val: 'all', label: language === 'fr' ? 'Tous' : 'All', count: filteredEmployees.length },
            { val: 'pending', label: language === 'fr' ? 'En attente' : 'Pending', count: approvalCounts.pending, color: 'text-amber-600' },
            { val: 'approved', label: language === 'fr' ? 'Validés' : 'Approved', count: approvalCounts.approved, color: 'text-green-600' },
            { val: 'rejected', label: language === 'fr' ? 'Rejetés' : 'Rejected', count: approvalCounts.rejected, color: 'text-red-600' },
          ].map(tab => (
            <button
              key={tab.val}
              onClick={() => setApprovalFilter(tab.val)}
              className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                approvalFilter === tab.val 
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900' 
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
              data-testid={`tab-${tab.val}`}
            >
              {tab.label}
              {tab.count > 0 && <span className={`ml-1 ${approvalFilter === tab.val ? '' : tab.color || ''}`}>({tab.count})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Mass action bar — only when records selected */}
      {selectedRecords.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800" data-testid="mass-action-bar">
          <Badge variant="secondary" className="text-xs">{selectedRecords.length} {language === 'fr' ? 'sélectionné(s)' : 'selected'}</Badge>
          <Button size="sm" className="gap-1 bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => handleValidation('approved')} data-testid="approve-btn">
            <CheckCircle size={13} /> {language === 'fr' ? 'Approuver' : 'Approve'}
          </Button>
          <Button size="sm" variant="destructive" className="gap-1 h-7 text-xs" onClick={() => handleValidation('rejected')} data-testid="reject-btn">
            <XCircle size={13} /> {language === 'fr' ? 'Rejeter' : 'Reject'}
          </Button>
          <Button size="sm" variant="outline" className="gap-1 h-7 text-xs ml-auto" onClick={() => setSelectedRecords([])} data-testid="clear-selection-btn">
            <X size={13} /> {language === 'fr' ? 'Annuler' : 'Clear'}
          </Button>
        </div>
      )}

      {/* Main Table — simplified, priority on hours/status/validation */}
      <Card className="border shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-primary" size={32} />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100/80 dark:bg-slate-800/80">
                    <TableHead className="w-8 py-2">
                      <Checkbox
                        checked={selectedRecords.length === filteredEmployees.length && filteredEmployees.length > 0}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="font-bold text-xs py-2">{t('employee')}</TableHead>
                    <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Quart' : 'Shift'}</TableHead>
                    <TableHead className="font-bold text-xs py-2 text-green-700 dark:text-green-400">{language === 'fr' ? 'Entrée' : 'In'}</TableHead>
                    <TableHead className="font-bold text-xs py-2 text-red-700 dark:text-red-400">{language === 'fr' ? 'Sortie' : 'Out'}</TableHead>
                    <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Durée' : 'Duration'}</TableHead>
                    <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Pause' : 'Break'}</TableHead>
                    <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Retard' : 'Late'}</TableHead>
                    <TableHead className="font-bold text-xs py-2">{language === 'fr' ? 'Validation' : 'Validation'}</TableHead>
                    <TableHead className="w-10 py-2"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-slate-500 dark:text-slate-400 py-8">{t('noData')}</TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((emp) => {
                      const record = dailyRecords[emp.employeeId];
                      const status = record?.approvalStatus || 'pending';
                      const rowBg = status === 'approved' ? 'bg-green-50/40 dark:bg-green-950/15' 
                        : status === 'rejected' ? 'bg-red-50/40 dark:bg-red-950/15' 
                        : !emp.isPresent ? 'bg-red-50/30 dark:bg-red-950/10'
                        : selectedRecords.includes(emp.employeeId) ? 'bg-blue-50/50 dark:bg-blue-950/20' 
                        : '';
                      return (
                        <TableRow 
                          key={emp.employeeId} 
                          className={`${rowBg} hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors`}
                          data-testid={`report-row-${emp.employeeCode}`}
                        >
                          <TableCell className="py-2">
                            <Checkbox
                              checked={selectedRecords.includes(emp.employeeId)}
                              onCheckedChange={() => toggleRecordSelection(emp.employeeId)}
                              data-testid={`select-${emp.employeeCode}`}
                            />
                          </TableCell>
                          <TableCell className="py-2">
                            <div>
                              <p className="font-medium text-sm text-slate-900 dark:text-slate-100">{emp.employeeName}</p>
                              <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">{emp.employeeCode}</p>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 text-xs text-slate-600 dark:text-slate-400">
                            {emp.shiftName ? (
                              <div>
                                <p className="font-medium text-blue-600 dark:text-blue-400">{emp.shiftName}</p>
                                <p className="text-[10px] text-slate-400">{emp.shiftStartTime}</p>
                              </div>
                            ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                          </TableCell>
                          <TableCell className="py-2 font-mono text-xs font-medium text-green-600 dark:text-green-400">{formatTime(emp.firstIn)}</TableCell>
                          <TableCell className="py-2 font-mono text-xs font-medium text-red-600 dark:text-red-400">{formatTime(emp.lastOut)}</TableCell>
                          <TableCell className="py-2">
                            {emp.totalHours > 0 ? (
                              <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{emp.totalHours}h</span>
                            ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                          </TableCell>
                          <TableCell className="py-2 text-xs text-slate-500 dark:text-slate-400">
                            {emp.breakMinutes > 0 ? formatDuration(emp.breakMinutes) : '-'}
                          </TableCell>
                          <TableCell className="py-2">
                            {emp.isLate ? (
                              <Badge className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[10px]">
                                +{emp.lateMinutes}min
                              </Badge>
                            ) : <span className="text-slate-300 dark:text-slate-600">-</span>}
                          </TableCell>
                          <TableCell className="py-2">{getApprovalBadge(emp.employeeId)}</TableCell>
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewDetails(emp)}
                              className="h-7 w-7 p-0"
                              data-testid={`view-details-${emp.employeeCode}`}
                            >
                              <ChevronRight size={16} className="text-slate-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photo Preview Dialog */}
      <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Image size={20} />
              {photoTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            {selectedPhoto && (
              <img src={selectedPhoto} alt={photoTitle} className="max-w-full max-h-[60vh] rounded-lg shadow-lg" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotoDialogOpen(false)}>
              {language === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Details — Side Panel */}
      <Sheet open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <SheetContent className="w-[400px] sm:w-[480px] overflow-y-auto" data-testid="details-panel">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <Clock size={18} />
              {selectedEmployee?.employeeName}
            </SheetTitle>
            <SheetDescription>
              {format(date, 'dd MMMM yyyy', { locale: language === 'fr' ? fr : enUS })} • {selectedEmployee?.employeeCode}
            </SheetDescription>
          </SheetHeader>
          
          <div className="mt-4 space-y-4">
            {/* Employee summary */}
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-center">
                <p className="text-lg font-bold text-green-700 dark:text-green-400">{formatTime(selectedEmployee?.firstIn)}</p>
                <p className="text-[10px] text-green-600 dark:text-green-500">{language === 'fr' ? 'Entrée' : 'In'}</p>
              </div>
              <div className="p-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-center">
                <p className="text-lg font-bold text-red-700 dark:text-red-400">{formatTime(selectedEmployee?.lastOut)}</p>
                <p className="text-[10px] text-red-600 dark:text-red-500">{language === 'fr' ? 'Sortie' : 'Out'}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-center">
                <p className="text-lg font-bold text-blue-700 dark:text-blue-400">{selectedEmployee?.totalHours || 0}h</p>
                <p className="text-[10px] text-blue-600 dark:text-blue-500">{language === 'fr' ? 'Durée' : 'Duration'}</p>
              </div>
            </div>

            {/* Validation status */}
            {selectedEmployee && (
              <div className="p-3 rounded-lg border dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">{language === 'fr' ? 'Statut validation' : 'Validation Status'}</span>
                  {getApprovalBadge(selectedEmployee.employeeId)}
                </div>
                {dailyRecords[selectedEmployee.employeeId]?.approvalComment && (
                  <div className="mt-2 p-2 bg-white dark:bg-slate-900 rounded border text-xs text-slate-600 dark:text-slate-400">
                    <Shield size={11} className="inline mr-1" />
                    {dailyRecords[selectedEmployee.employeeId].approvalComment}
                  </div>
                )}
              </div>
            )}

            {/* Photos */}
            {(selectedEmployee?.entryPhotoUrl || selectedEmployee?.exitPhotoUrl) && (
              <div className="grid grid-cols-2 gap-2">
                {selectedEmployee?.entryPhotoUrl && (
                  <button onClick={() => handleViewPhoto(selectedEmployee.entryPhotoUrl, `${language === 'fr' ? 'Entrée' : 'Entry'} - ${selectedEmployee.employeeName}`)} className="group">
                    <img src={`${BACKEND_URL}${selectedEmployee.entryPhotoUrl}`} alt="Entry" className="w-full h-24 object-cover rounded-lg border group-hover:border-primary transition-colors" />
                    <p className="text-[10px] text-slate-400 text-center mt-1">{language === 'fr' ? 'Photo entrée' : 'Entry photo'}</p>
                  </button>
                )}
                {selectedEmployee?.exitPhotoUrl && (
                  <button onClick={() => handleViewPhoto(selectedEmployee.exitPhotoUrl, `${language === 'fr' ? 'Sortie' : 'Exit'} - ${selectedEmployee.employeeName}`)} className="group">
                    <img src={`${BACKEND_URL}${selectedEmployee.exitPhotoUrl}`} alt="Exit" className="w-full h-24 object-cover rounded-lg border group-hover:border-primary transition-colors" />
                    <p className="text-[10px] text-slate-400 text-center mt-1">{language === 'fr' ? 'Photo sortie' : 'Exit photo'}</p>
                  </button>
                )}
              </div>
            )}

            {/* Punch timeline */}
            <div>
              <h4 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-2">{language === 'fr' ? 'Pointages' : 'Punches'}</h4>
              {selectedEmployee?.allPunches?.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-4">{language === 'fr' ? 'Aucun pointage' : 'No punches'}</p>
              ) : (
                <div className="space-y-1">
                  {selectedEmployee?.allPunches?.map((punch, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/40 group transition-colors" data-testid={`punch-row-${index}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        punch.eventType === 'IN' ? 'bg-green-100 dark:bg-green-900/40' :
                        punch.eventType === 'OUT' ? 'bg-red-100 dark:bg-red-900/40' :
                        punch.eventType === 'BREAK_START' ? 'bg-orange-100 dark:bg-orange-900/40' :
                        'bg-blue-100 dark:bg-blue-900/40'
                      }`}>
                        {getPunchIcon(punch.eventType)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{getPunchTypeLabel(punch.eventType)}</p>
                        <p className="font-mono text-xs text-slate-500 dark:text-slate-400">{formatTime(punch.timestamp)}</p>
                      </div>
                      {punch.photoUrl && (
                        <button onClick={() => handleViewPhoto(punch.photoUrl, `${getPunchTypeLabel(punch.eventType)} - ${selectedEmployee.employeeName}`)} className="text-blue-500 hover:underline text-xs">
                          <Camera size={14} />
                        </button>
                      )}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleOpenCorrection(punch)} title={language === 'fr' ? 'Corriger' : 'Correct'}>
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" onClick={() => handleDeletePunch(punch)} title={language === 'fr' ? 'Supprimer' : 'Delete'}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick actions in panel */}
            {selectedEmployee && (
              <div className="flex gap-2 pt-3 border-t dark:border-slate-700">
                <Button
                  size="sm"
                  className="flex-1 gap-1 bg-green-600 hover:bg-green-700 h-8 text-xs"
                  onClick={() => {
                    setSelectedRecords([selectedEmployee.employeeId]);
                    handleValidation('approved');
                  }}
                  disabled={(dailyRecords[selectedEmployee.employeeId]?.approvalStatus) === 'approved'}
                  data-testid="panel-approve-btn"
                >
                  <CheckCircle size={13} /> {language === 'fr' ? 'Approuver' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1 gap-1 h-8 text-xs"
                  onClick={() => {
                    setSelectedRecords([selectedEmployee.employeeId]);
                    handleValidation('rejected');
                  }}
                  disabled={(dailyRecords[selectedEmployee.employeeId]?.approvalStatus) === 'rejected'}
                  data-testid="panel-reject-btn"
                >
                  <XCircle size={13} /> {language === 'fr' ? 'Rejeter' : 'Reject'}
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Punch Correction Dialog */}
      <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit size={20} />
              {language === 'fr' ? 'Corriger le pointage' : 'Correct Punch'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Les corrections sont enregistrées dans le journal d\'audit.' 
                : 'Corrections are logged in the audit trail.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Heure' : 'Time'}</Label>
              <Input
                type="datetime-local"
                value={correctionData.newTimestamp}
                onChange={(e) => setCorrectionData({ ...correctionData, newTimestamp: e.target.value })}
                data-testid="correction-timestamp"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de punch' : 'Punch Type'}</Label>
              <Select 
                value={correctionData.newEventType} 
                onValueChange={(value) => setCorrectionData({ ...correctionData, newEventType: value })}
              >
                <SelectTrigger data-testid="correction-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN">{getPunchTypeLabel('IN')}</SelectItem>
                  <SelectItem value="BREAK_START">{getPunchTypeLabel('BREAK_START')}</SelectItem>
                  <SelectItem value="BREAK_END">{getPunchTypeLabel('BREAK_END')}</SelectItem>
                  <SelectItem value="OUT">{getPunchTypeLabel('OUT')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Raison de la correction *' : 'Reason for correction *'}</Label>
              <Textarea
                value={correctionData.reason}
                onChange={(e) => setCorrectionData({ ...correctionData, reason: e.target.value })}
                placeholder={language === 'fr' ? 'Ex: Erreur de pointage...' : 'Ex: Punch error...'}
                rows={3}
                data-testid="correction-reason"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCorrectPunch} className="gap-2" data-testid="save-correction-btn">
              <Save size={16} />
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Validation Dialog */}
      <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {validationAction === 'approved' ? (
                <>
                  <CheckCircle size={20} className="text-green-600" />
                  {language === 'fr' ? 'Valider les heures' : 'Approve Hours'}
                </>
              ) : (
                <>
                  <XCircle size={20} className="text-red-600" />
                  {language === 'fr' ? 'Rejeter les heures' : 'Reject Hours'}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? `${selectedRecords.length} employé(s) sélectionné(s)`
                : `${selectedRecords.length} employee(s) selected`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                {language === 'fr' ? 'Commentaire' : 'Comment'}
                {validationAction === 'rejected' && <span className="text-red-500"> *</span>}
              </Label>
              <Textarea
                value={validationComment}
                onChange={(e) => setValidationComment(e.target.value)}
                placeholder={
                  validationAction === 'approved'
                    ? (language === 'fr' ? 'Commentaire optionnel...' : 'Optional comment...')
                    : (language === 'fr' ? 'Raison du rejet (obligatoire)...' : 'Reason for rejection (required)...')
                }
                rows={3}
                data-testid="validation-comment"
              />
            </div>
            
            {validationAction === 'rejected' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  {language === 'fr' 
                    ? 'Un commentaire est obligatoire pour rejeter les heures.'
                    : 'A comment is required when rejecting hours.'}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidationDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={submitValidation} 
              className={`gap-2 ${validationAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}`}
              variant={validationAction === 'rejected' ? 'destructive' : 'default'}
              disabled={validating}
              data-testid="confirm-validation-btn"
            >
              {validating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : validationAction === 'approved' ? (
                <CheckCircle size={16} />
              ) : (
                <XCircle size={16} />
              )}
              {validationAction === 'approved' 
                ? (language === 'fr' ? 'Valider' : 'Approve')
                : (language === 'fr' ? 'Rejeter' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
